// Session bootstrap for a P2P joiner (docs/p2p.md §"Session info").
//
// A share link is just `?join=<sessionId>` — it carries no list of mods. Before
// a joiner can fetch and run the host's client halves, it must learn WHICH mods
// the host is running. It asks the host directly, over the same broker-relayed
// WebRTC path the game buses use: a throwaway peer connection whose only traffic
// is one text request and one text reply. The broker stays a blind relay — it
// never sees or stores the mod list; the host (which owns the game) is the
// source of truth for what it's running.
//
//   joiner → host  : { "t": "info" }
//   host   → joiner: { "t": "mods", "mode": "p2p"|"server",
//                      "urls": [ ...ws URLs... ], "romHash": "…"|null }
//
// The peer is torn down the instant the reply lands; the joiner then opens the
// real game buses against those URLs. This mirrors the join handshake exactly —
// same offer/answer, same broker signalOffer — only the payload differs (a
// metadata Q&A instead of the game data channels), so it needs no new broker
// message and no broker-held state.
//
// `mode` tells the joiner where the game actually is. "p2p": the inviter's
// browser is the server, so the buses connect P2P to it (the current behavior).
// "server": the inviter is just another client of a shared server, so the buses
// connect to that SERVER (urls is the game's deployment-root), and the friend is
// routed by the server like any fresh player. Absent → "p2p" (older host).

import { brokerJoinSignal, type BrokerSignalDeps } from "./broker-signal.js";

// Cap ICE gathering before signaling with whatever candidates we have (a
// reachable host connects on host candidates alone). Mirrors WebRtcBusSocket.
const ICE_GATHER_TIMEOUT_MS = 3000;
// Bound the whole exchange so a dead or silent host fails the load instead of
// hanging it. Covers signaling + connect + the one-frame Q&A.
const INFO_TIMEOUT_MS = 12_000;

// A public STUN server so a joiner behind NAT can still reach a remote host; on
// a LAN/localhost host candidates alone connect. Mirrors WebRtcBusSocket.
const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
];

export interface SessionInfoDeps extends BrokerSignalDeps {
  // Defaults to the DOM RTCPeerConnection. Tests inject node-datachannel's.
  createPeerConnection?: (config: RTCConfiguration) => RTCPeerConnection;
  iceServers?: RTCIceServer[];
  // Signaling transport (offer → answer). Default relays through the broker,
  // exactly like a game bus; tests point it straight at the host's acceptOffer.
  signalOffer?: (offerSdp: string) => Promise<string>;
}

// The session metadata a joiner learns from the host: how to connect ("p2p" to
// the host vs "server" to a shared server), which mods to run, and the ROM hash
// to enforce (null when the host hasn't loaded a ROM yet).
export interface JoinerSessionInfo {
  mode: "p2p" | "server";
  modUrls: string[];
  romHash: string | null;
}

// Ask the host behind broker `session` what it's running (mod URLs + ROM hash).
// Resolves with the metadata; rejects on signaling/connection failure or if the
// host doesn't answer within INFO_TIMEOUT_MS.
export async function requestSessionInfo(
  brokerUrl: string,
  session: string,
  deps: SessionInfoDeps = {},
): Promise<JoinerSessionInfo> {
  const makePc = deps.createPeerConnection ?? ((c) => new RTCPeerConnection(c));
  const signalOffer = deps.signalOffer ?? brokerJoinSignal(brokerUrl, session, deps);
  const pc = makePc({ iceServers: deps.iceServers ?? DEFAULT_ICE_SERVERS });
  // We're the offerer, so we create the reliable channel; the host adopts it by
  // label (RtcHostPeer). One channel is enough — this exchange is text-only.
  const chan = pc.createDataChannel("reliable", { ordered: true });

  return new Promise<JoinerSessionInfo>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
      try { pc.close(); } catch { /* already gone */ }
    };
    const timer = setTimeout(
      () => finish(() => reject(new Error("session-info timed out"))),
      INFO_TIMEOUT_MS,
    );

    chan.onopen = () => {
      try { chan.send(JSON.stringify({ t: "info" })); }
      catch (e) { finish(() => reject(toError(e))); }
    };
    chan.onmessage = (ev: MessageEvent) => {
      const info = parseModsReply(ev.data);
      if (info) finish(() => resolve(info));
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "failed" || st === "closed" || st === "disconnected") {
        finish(() => reject(new Error("session-info connection lost")));
      }
    };

    void exchange(pc, signalOffer).catch((e: unknown) => finish(() => reject(toError(e))));
  });
}

// Offer/answer, non-trickle (gather fully, then signal) — the joiner half of the
// handshake, identical to WebRtcBusSocket's minus the data channels.
async function exchange(
  pc: RTCPeerConnection,
  signalOffer: (offerSdp: string) => Promise<string>,
): Promise<void> {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await iceGatheringComplete(pc);
  const localSdp = pc.localDescription?.sdp ?? offer.sdp ?? "";
  const answerSdp = await signalOffer(localSdp);
  await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
}

function iceGatheringComplete(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      pc.removeEventListener("icegatheringstatechange", onChange);
      clearTimeout(timer);
      resolve();
    };
    const onChange = (): void => {
      if (pc.iceGatheringState === "complete") finish();
    };
    pc.addEventListener("icegatheringstatechange", onChange);
    const timer = setTimeout(finish, ICE_GATHER_TIMEOUT_MS);
  });
}

// Normalize a thrown/rejected value to an Error for a Promise rejection reason.
function toError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

// Parse a host reply, tolerating anything that isn't a well-formed mods message
// (returns null → keep waiting). URLs are filtered to strings; romHash is a
// string or null (absent/older host → null, i.e. no ROM enforcement); mode is
// "server" only when explicitly stated, else "p2p" (the default / older host).
function parseModsReply(data: unknown): JoinerSessionInfo | null {
  if (typeof data !== "string") return null;
  let msg: unknown;
  try { msg = JSON.parse(data); } catch { return null; }
  if (!msg || typeof msg !== "object") return null;
  const m = msg as { t?: unknown; mode?: unknown; urls?: unknown; romHash?: unknown };
  if (m.t !== "mods" || !Array.isArray(m.urls)) return null;
  return {
    mode: m.mode === "server" ? "server" : "p2p",
    modUrls: m.urls.filter((u): u is string => typeof u === "string"),
    romHash: typeof m.romHash === "string" ? m.romHash : null,
  };
}
